import type { AnnouncementBanner } from 'types/announcements';
import {
  type CatalogProductCard,
  type EmailDraftCardData,
  type KnowledgeSource,
  type OutfitProduct,
  type SuggestedAction,
} from '../../../types/ai';
import type { AdminFeedbackType } from '../../../types/feedback';

export type ChatRole = 'user' | 'assistant';

export type ChatMessageItem = {
  id: string;
  role: ChatRole;
  content: string;
  imageUrl?: string;
  createdAt: string;
  sources?: KnowledgeSource[];
  orderContext?: Record<string, unknown> | null;
  responseMode?: string;
  suppressOrderContext?: boolean;
  outfitProducts?: OutfitProduct[];
  outfitSlotsExpected?: number;
  isOutfitBuilding?: boolean;
  isProductSearching?: boolean;
  // isProductSearching släcks när turen är klar. Den här stannar kvar och säger
  // vad turen HANDLADE om, även när sökningen slutade i en motfråga utan kort.
  searchedProducts?: boolean;
  productCardsExpected?: number;
  productCards?: CatalogProductCard[];
  selectedProducts?: OutfitProduct[];
  routing?: Record<string, unknown>;
  status?: 'success' | 'error';
  suggestedActions?: SuggestedAction[];
  detectedLanguage?: string;
  emailDraft?: EmailDraftCardData;
  announcement?: AnnouncementBanner;
};

export const isWelcomeMessage = (
  message: Pick<ChatMessageItem, 'id' | 'role' | 'suggestedActions'>,
) => {
  if (message.id === 'welcome' || !message.id) {
    return true;
  }

  if (message.role !== 'assistant' || !Array.isArray(message.suggestedActions)) {
    return false;
  }

  return message.suggestedActions.some((action) =>
    String(action.actionId ?? '')
      .toLowerCase()
      .startsWith('welcome.'),
  );
};

export type ChatMessageProps = {
  message: ChatMessageItem;
  onSuggestedActionClick?: (action: SuggestedAction) => void;
  isLatestAssistant?: boolean;
  sessionId?: string;
  channel?: string;
  showAdminFeedback?: boolean;
  onAdminFeedback?: (type: AdminFeedbackType) => void;
  onEmailDraftResolved?: (
    message: string,
    status: 'confirmed' | 'already_sent' | 'cancelled',
  ) => void;
};

type OrderRowLike = {
  quantity?: number;
  unitPrice?: {
    tax?: {
      priceIncludingTax?: number;
      currency?: string;
    };
    currency?: string;
  };
  product?: {
    id?: string;
    imageUrl?: string;
    productUrl?: string;
    url?: string;
    name?: Array<{ value?: string }> | string;
    brand?: { name?: string } | string;
  };
};

type CustomerOrderListLike = Array<{
  orderNumber?: string | number;
  number?: string | number;
  id?: string | number;
  orderId?: string | number;
  createdAt?: string;
  currency?: string;
  rows?: OrderRowLike[];
}>;

// Backend har levererat kundens ordrar under fyra olika namn genom åren. Alla fyra
// förekommer i produktionsdata — läs dem via selektorerna i parsers/order-context.
export type CustomerDetailsLike = {
  orders?: CustomerOrderListLike;
  recentOrders?: CustomerOrderListLike;
  allOrders?: CustomerOrderListLike;
  ordersCount?: number;
};

export type CustomerOrderLike = NonNullable<CustomerDetailsLike['orders']>[number];

export type SliderProductLike = {
  id?: string | undefined;
  selectionKey?: string | undefined;
  image?: string;
  brand?: string | undefined;
  name: string;
  size?: string | undefined;
  sizes?:
    | Array<{
        label?: string;
        size?: string;
        eu?: string;
        us?: string;
        uk?: string;
        stock?: number;
        buyable?: boolean;
      }>
    | string[]
    | string;
  price?: string | number | undefined;
  currency?: string | undefined;
  url?: string | undefined;
  color?: string | undefined;
  material?: string | undefined;
  slot?: string | undefined;
  /** Try-on-behörighet, avgjord i backend. Klienten bedömer aldrig själv. */
  tryOn?: boolean | undefined;
  /** Bara för skor; saknas = helkropp. */
  tryOnFraming?: 'feet' | undefined;
};

export type ProductCardData = {
  image: string;
  brand: string;
  title: string;
  sizes?: string;
  price: string;
  url: string;
  id?: string;
  slot?: string;
  color?: string;
  material?: string;
  tryOn?: boolean;
  tryOnFraming?: 'feet';
  reason?: string;
  regularPrice?: number;
  discountPercent?: number;
};

export type ProductActionButton = { label: string; value: string; requiresSize?: boolean };
export type ProductSliderProductView = SliderProductLike & {
  tagLabel?: string;
  actionButtons?: ProductActionButton[];
};
